import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Description as DocumentIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Pending as PendingIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

const ImmigrationTab: React.FC = () => {
  const theme = useTheme();

  const documents = [
    {
      name: "I-9 Employment Eligibility Verification",
      status: "Completed",
      dueDate: "Completed on 2024-01-15",
      required: true,
    },
    {
      name: "H-1B Visa Documentation",
      status: "Approved",
      dueDate: "Valid until 2025-12-31",
      required: true,
    },
    {
      name: "Social Security Card Copy",
      status: "Submitted",
      dueDate: "Under Review",
      required: true,
    },
    {
      name: "Passport Copy",
      status: "Pending",
      dueDate: "Due: 2024-02-01",
      required: false,
    },
  ];

  const visaStatus = {
    type: "H-1B",
    status: "Active",
    validUntil: "2025-12-31",
    remainingDays: 385,
    progress: 65,
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "approved":
        return "success";
      case "submitted":
        return "info";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "approved":
        return <CheckIcon />;
      case "submitted":
        return <PendingIcon />;
      case "pending":
        return <WarningIcon />;
      default:
        return <DocumentIcon />;
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Visa Status Card */}
      <Grid item xs={12} md={6}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <DocumentIcon color="primary" sx={{ mr: 1 }} />
              Visa Status
            </Typography>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Type:</strong> {visaStatus.type}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong>
                <Chip
                  label={visaStatus.status}
                  color="success"
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Valid Until:</strong> {visaStatus.validUntil}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Remaining Days:</strong> {visaStatus.remainingDays}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom>
                Visa Progress ({visaStatus.progress}%)
              </Typography>
              <LinearProgress
                variant="determinate"
                value={visaStatus.progress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12} md={6}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Upload Document
              </Button>
              <Button
                variant="outlined"
                startIcon={<DocumentIcon />}
                fullWidth
              >
                Request Visa Extension
              </Button>
              <Button
                variant="outlined"
                startIcon={<CheckIcon />}
                fullWidth
              >
                View I-9 Form
              </Button>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Documents List */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <DocumentIcon color="primary" sx={{ mr: 1 }} />
              Immigration Documents
            </Typography>
            <List>
              {documents.map((doc, index) => (
                <ListItem
                  key={index}
                  sx={{
                    border: 1,
                    borderColor: alpha(theme.palette.divider, 0.2),
                    borderRadius: 2,
                    mb: 1,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemIcon>
                    {getStatusIcon(doc.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">
                          {doc.name}
                        </Typography>
                        {doc.required && (
                          <Chip
                            label="Required"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={doc.dueDate}
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={doc.status}
                      color={getStatusColor(doc.status) as any}
                      size="small"
                      variant="outlined"
                    />
                    <Button size="small" variant="text">
                      View
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </StyledCard>
      </Grid>
    </Grid>
  );
};

export default ImmigrationTab;